"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  loadTokens,
  type TosAcceptanceStatus,
} from "@/lib/api";

const SESSION_DISMISS_KEY = "granbay.tos.dismissedThisSession";

/**
 * EPIC-008 STORY-808 — re-prompt banner shown when the user hasn't
 * accepted the currently effective ToS.
 *
 * Rendered globally from layout.tsx (below the BetaBanner). Polls
 * /v1/me/tos/status on mount when authed; renders the banner when
 * `acceptedThisVersion` is false. User can either review (links to
 * /legal/tos), accept inline (single click writes a tos_acceptances
 * row), or dismiss (sessionStorage suppresses the banner until next
 * tab — they'll see it again on next visit).
 *
 * Never shown to unauthenticated visitors (signup flow has its own
 * checkbox path).
 */
export function TosRePrompt() {
  const { t } = useTranslation();

  const [status, setStatus] = useState<TosAcceptanceStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!loadTokens()) return;
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "true") {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDismissed(true);
        return;
      }
    } catch {
      // sessionStorage blocked (private mode); fall through and
      // render anyway
    }
    let cancelled = false;
    void api
      .getTosStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        // Non-fatal: if the endpoint fails (network blip / 401 from
        // expired token mid-load) just don't render. We're not the
        // primary auth path.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAccept() {
    if (!status || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.acceptTos(status.current.id);
      setStatus({
        ...status,
        acceptedThisVersion: true,
        acceptedAt: new Date().toISOString(),
        acceptedVersion: status.current.version,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("tosRePrompt.saveFailed");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function handleDismiss() {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "true");
    } catch {
      // sessionStorage blocked — accept the dismissal in-memory only.
    }
    setDismissed(true);
  }

  if (dismissed) return null;
  if (!status) return null;
  if (status.acceptedThisVersion) return null;

  return (
    <div
      role="alertdialog"
      aria-labelledby="tos-reprompt-title"
      className="w-full border-b text-[12px]"
      style={{
        borderColor: "var(--color-border)",
        background:
          "color-mix(in srgb, var(--color-status-warning, #ffb800) 10%, var(--color-bg))",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-5 py-2">
        <div className="min-w-0 flex-1">
          <span
            id="tos-reprompt-title"
            className="font-bold text-[var(--color-text)]"
          >
            {t("tosRePrompt.title")}
          </span>{" "}
          <span className="text-[var(--color-text2)]">
            {t("tosRePrompt.body")}
          </span>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Link
            href="/legal/tos"
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1 font-bold text-[var(--color-text2)] hover:border-[var(--color-brand)]"
          >
            {t("tosRePrompt.review")}
          </Link>
          <button
            type="button"
            disabled={submitting}
            onClick={handleAccept}
            className="rounded-md px-2.5 py-1 font-bold text-white disabled:opacity-50"
            style={{ background: "var(--color-brand)" }}
          >
            {submitting ? t("tosRePrompt.accepting") : t("tosRePrompt.accept")}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={submitting}
            aria-label={t("tosRePrompt.dismiss")}
            className="rounded-md border border-[var(--color-border)] px-2 py-1 font-bold text-[var(--color-text3)] hover:text-[var(--color-text)]"
          >
            ✕
          </button>
        </div>
      </div>
      {error && (
        <p
          role="alert"
          className="mx-auto max-w-[1400px] px-5 pb-2 text-[11px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
